import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { authApi } from "../../services/api";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // ============================================
  // TanStack Query - Verify Email Mutation
  // ============================================
  const verifyEmailMutation = useMutation({
    mutationFn: (verificationToken) => authApi.verifyEmail(verificationToken),
  });

  const { mutate, isIdle, isPending, isSuccess, error } = verifyEmailMutation;

  useEffect(() => {
    // Only trigger if we have a token and mutation hasn't been triggered yet
    if (token && isIdle) {
      mutate(token);
    }
  }, [token, isIdle, mutate]);

  const handleGoToLogin = () => {
    navigate("/login");
  };

  // No token provided
  if (!token) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8 text-destructive" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("auth.verifyEmail.errorTitle", "Verification Failed")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("auth.verifyEmail.noToken", "No verification token provided.")}
        </p>
        <Button onClick={handleGoToLogin}>
          {t("auth.verifyEmail.goToLogin", "Go to Login")}
        </Button>
      </div>
    );
  }

  // Loading state
  if (isPending || isIdle) {
    return (
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-foreground">
          {t("auth.verifyEmail.verifying", "Verifying your email...")}
        </h2>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-500/20 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">
          {t("auth.verifyEmail.successTitle", "Email Verified!")}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t(
            "auth.verifyEmail.successMessage",
            "Your email has been verified. You can now log in to your account.",
          )}
        </p>
        <Button onClick={handleGoToLogin}>
          {t("auth.verifyEmail.goToLogin", "Go to Login")}
        </Button>
      </div>
    );
  }

  // Error state
  const errorMessage =
    error?.response?.data?.error?.message ||
    error?.response?.data?.error ||
    t(
      "auth.verifyEmail.errorMessage",
      "Failed to verify email. The link may have expired.",
    );

  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <XCircle className="w-8 h-8 text-destructive" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-2">
        {t("auth.verifyEmail.errorTitle", "Verification Failed")}
      </h2>
      <p className="text-muted-foreground mb-6">{errorMessage}</p>
      <div className="space-y-3">
        <Button onClick={handleGoToLogin}>
          {t("auth.verifyEmail.goToLogin", "Go to Login")}
        </Button>
        <p className="text-sm text-muted-foreground">
          {t("auth.verifyEmail.needNewLink", "Need a new verification link?")}{" "}
          <Link
            to="/login"
            className="text-primary hover:text-primary/80 font-medium"
          >
            {t("auth.verifyEmail.loginToResend", "Log in to request one")}
          </Link>
        </p>
      </div>
    </div>
  );
}
